import React, { useEffect, useState } from 'react';
import Sheet from '@mui/joy/Sheet';
import Skeleton from 'react-loading-skeleton';
import CssBaseline from '@mui/joy/CssBaseline';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import useUserByEmail from '../../../store/ReactQueries/users/useUserByEmail';
import useGetTicketsClosedToday from '../../../store/ReactQueries/dashboards/useGetTicketsClosedToday';
import useGetOpenTicketsByMonth from '../../../store/ReactQueries/dashboards/useGetOpenTicketsByMonth';
import useGetClosedTicketsByMonth from '../../../store/ReactQueries/dashboards/useGetClosedTicketsByMonth';
import { useUserStore } from '../../../store/useUserStore';
import { darkModeBackgroud } from '../../../utils/constants';
import { useDarkModeStore } from '../../../store/useDarkModeStore';
import { useLanguageStore } from '../../../store/useLanguageStore';

interface ProfileUserCardProps {
    email?: string;
    mode: "ADD" | "EDIT" | "VIEW" | "PROFILE";
}

export const ProfileUserCard: React.FC<ProfileUserCardProps> = ({ email, mode }) => {
    const year = new Date().getFullYear();
    const add = mode === 'ADD';

    const { user } = useUserStore();
    const { darkMode } = useDarkModeStore();
    const { language } = useLanguageStore();
    const { data: userById } = useUserByEmail(email ?? '');
    const { data: ticketClosedToday } = useGetTicketsClosedToday();
    const { data: openTicketsByMonth } = useGetOpenTicketsByMonth(year);
    const { data: closedTicketsByMonth } = useGetClosedTicketsByMonth(year);
    const [loading, setLoading] = useState<boolean>(false);

    const openTickets = openTicketsByMonth?.reduce((sum, ticket) => sum + ticket.ticketCount, 0) ?? 0;
    const closedTickets = closedTicketsByMonth?.reduce((sum, ticket) => sum + ticket.ticketCount, 0) ?? 0;

    const items: { id: number, label: string, value: number, color: string }[] = [
        { id: 1, label: 'OpenTicket', value: openTickets, color: '#EEA226' },
        { id: 2, label: 'ClosedTicket', value: closedTickets, color: '#6FC944' },
        { id: 3, label: 'ClosedTicketsToday', value: ticketClosedToday ?? 0, color: '#899DB7' },
    ];

    const isEmployee = userById && !userById.roles?.includes('Admin');

    useEffect(() => {
        setLoading(true);

        const timer = setTimeout(() => {
            setLoading(false);
        }, 1000);

        return () => clearTimeout(timer);
    }, []);

    return (
        <div className='profile-user-card'>
            <CssBaseline />
            <Sheet
                sx={{
                    ...styleSheetProfileCard,
                    border: '1px solid rgba(50, 71, 92, 0.12)',
                    background: darkMode ? darkModeBackgroud : 'white',
                }}
            >
                <div
                    className='d-flex flex-column align-items-center justify-content-between gap-2'
                    style={{ height: '-webkit-fill-available', padding: '20px 0' }}
                >
                    <div className='d-flex flex-column align-items-center gap-2'>
                        <AccountCircleIcon sx={{ width: 150, height: 150, color: darkMode ? 'white' : '#C1CCD5' }} />
                        <span
                            className='fw-bolder fs-5'
                            style={{ color: darkMode ? 'white' : 'initial' }}
                        >
                            {
                                loading
                                    ? <Skeleton style={{ width: 120, height: 30 }} />
                                    : add
                                        ? language['NewUser']
                                        : isEmployee
                                            ? `${userById.firstname ?? ''} ${userById.lastname ?? ''}`
                                            : user ? `${user?.fullname ?? ''}` : ''
                            }
                        </span>
                        <span style={{ color: darkMode ? 'white' : '#C1CCD5', fontSize: 14, fontWeight: 'bold' }}>
                            {
                                loading
                                    ? <Skeleton style={{ width: 70, height: 21 }} />
                                    : add
                                        ? ''
                                        : isEmployee
                                            ? `${userById?.roles}`
                                            : user?.['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] ?? ''
                            }
                        </span>
                    </div>
                    {
                        (!userById && !add)
                        && <div className='d-flex flex-column m-0 p-0 w-100'>
                            {
                                loading
                                    ? [1, 2, 3].map(item => <Skeleton key={`${item}-ticket-loading-inputs`} style={{ width: '100%', height: 56 }} />)
                                    : items.map(({ id, value, label, color }, index) =>
                                        <div
                                            key={id}
                                            className={`${index === 0 && 'border-top'} border-bottom d-flex flex-row`}
                                            style={{ padding: '15px 20px' }}
                                        >
                                            <span style={{ color: darkMode ? 'white' : 'initial' }}>{language[label] ?? label}</span>
                                            <span className='ms-auto fw-bold' style={{ color }}>{value}</span>
                                        </div>
                                    )
                            }
                        </div>
                    }

                    <div className='d-flex flex-column align-items-center w-100 gap-2' style={{ padding: '10px 25px' }}>
                    </div>
                </div>
            </Sheet>
        </div>
    );
}
export default ProfileUserCard;

export const styleSheetProfileCard = {
    my: 0,
    py: 2,
    px: 0,
    gap: 0,
    width: '100%',
    display: 'flex',
    borderRadius: 'md',
    position: 'relative',
    flexDirection: 'column',
    height: 'calc(100vh - 150px)',
    boxShadow: '8px 8px 30px rgba(0, 0, 0, 0.2)',
}